import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { DeleteWithConfirmationDialog } from "@impero-com/common/components/deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { PopupSwitch } from "@impero-com/common/components/popupSwitch";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { scrollToFirstErroredElement } from "@impero-com/common/utils/form/erroredElements";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Button } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { TestingProgramViewDto as TestingProgramDto } from "api/generated/model";
import { FieldCondition } from "components/fieldBuilder/models";
import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { ViewCancelButton } from "components/stickyFooter/ViewCancelButton";
import deleteIcon from "img/delete.svg";
import {
    buildTestingProgramTestingControlsListUrl,
    testingProgramIdParamSchema,
    testingProgramListUrl,
} from "org/controlTesting/urlUtils";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";

import { useTestingProgramViewAnchors } from "./consts";
import { useDeleteTestingProgramMutation } from "./queries/useDeleteTestingProgramMutation";
import { useTestingProgramQuery } from "./queries/useTestingProgramQuery";
import { useUpdateTestingProgramMutation } from "./queries/useUpdateTestingProgramMutation";
import { TestingProgramForm } from "./TestingProgramForm";
import {
    TestingProgramFormSchema,
    useTestingProgramViewFormSchema,
} from "./useTestingProgramViewFormSchema";
import {
    apiAccessRightToFormAccessRights,
    apiControlConclusionFieldConditionToFormFieldCondition,
    apiControlConclusionFieldKindToFormConclusionFieldKind,
    apiSampleConclusionFieldConditionToFormFieldCondition,
    formUpdateTestingProgramToApiUpdatableTestingProgram,
} from "./utils";

export function TestingProgramEditPage() {
    const params = useParams();
    const unparsedTestingProgramId = params.id;
    const testingProgramIdParseResult = testingProgramIdParamSchema.safeParse(
        unparsedTestingProgramId
    );
    if (testingProgramIdParseResult.success === false) {
        return <LoggedInHTTPError code={400} />;
    }
    const testingProgramId = testingProgramIdParseResult.data;

    return <TestingProgramEdit testingProgramId={testingProgramId} />;
}

interface TestingProgramEditProps {
    testingProgramId: number;
}

function TestingProgramEdit({ testingProgramId }: TestingProgramEditProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const anchors = useTestingProgramViewAnchors();

    const [activePopup, setActivePopup] = useState<{
        id: "DELETE";
        props: DeleteTestingProgramPopupActions;
    } | null>(null);
    const { mutate: deleteTestingProgram } = useDeleteTestingProgramMutation();
    const { data } = useTestingProgramQuery(testingProgramId);

    return (
        <OrgApplicationBar
            title={{
                title: data.content.resource.name,
                breadCrumbs: [
                    {
                        label: t("ControlTesting_ModuleTitle"),
                        url: testingProgramListUrl,
                    },
                ],
            }}
            navigation={{
                type: "Anchors",
                anchors,
            }}
            actions={{
                id: "testingProgramEdit",
                actions: [
                    {
                        id: "testingProgramDelete",
                        label: t("ControlTesting_ProgramDelete"),
                        icon: { svg: deleteIcon },
                        onClick: () =>
                            setActivePopup({
                                id: "DELETE",
                                props: {
                                    onCancel: () => setActivePopup(null),
                                    onConfirm: () => {
                                        setActivePopup(null);
                                        deleteTestingProgram({
                                            testingProgramId,
                                        });
                                        navigate(testingProgramListUrl, {
                                            replace: true,
                                        });
                                    },
                                },
                            }),
                        status: data.content.actions.delete
                            ? { type: "ENABLED" }
                            : {
                                  type: "DISABLED",
                                  reason: t(
                                      "ControlTesting_ProgramDelete_Disabled"
                                  ),
                              },
                    },
                ],
            }}
        >
            <PopupSwitch
                specs={{
                    DELETE: DeleteTestingProgramPopup,
                }}
                active={activePopup}
            />
            <View
                testingProgramId={testingProgramId}
                testingProgramDto={data}
            />
        </OrgApplicationBar>
    );
}

interface ViewProps {
    testingProgramId: number;
    testingProgramDto: TestingProgramDto;
}

function View({ testingProgramId, testingProgramDto }: ViewProps) {
    const { t } = useTranslation();
    const { mutateAsync, isPending } = useUpdateTestingProgramMutation();
    const schema = useTestingProgramViewFormSchema(testingProgramId);
    const navigate = useNavigate();

    const values: TestingProgramFormSchema = {
        name: testingProgramDto.content.resource.name,
        description: testingProgramDto.content.resource.description,
        accessRights: apiAccessRightToFormAccessRights(
            testingProgramDto.content.resource.accessRights
        ),
        controlConclusionTasks:
            testingProgramDto.content.resource.controlConclusionFields.fields.map(
                (field) => {
                    let condition: FieldCondition<string> | null = null;
                    if (field.condition) {
                        const targetField =
                            testingProgramDto.content.resource.controlConclusionFields.fields.find(
                                (f) => f.id === field.condition!.fieldId
                            );
                        if (targetField === undefined) {
                            throw new Error(
                                "Condition on a field that does not exist"
                            );
                        }
                        condition =
                            apiControlConclusionFieldConditionToFormFieldCondition(
                                targetField,
                                field.condition
                            );
                    }
                    return {
                        ...field,
                        condition,
                        fieldType:
                            apiControlConclusionFieldKindToFormConclusionFieldKind(
                                field.fieldType
                            ),
                    };
                }
            ),
        sampleConclusionTasks:
            testingProgramDto.content.resource.sampleConclusionFields.fields.map(
                (field) => {
                    return {
                        ...field,
                        condition: field.condition
                            ? apiSampleConclusionFieldConditionToFormFieldCondition(
                                  field.condition
                              )
                            : null,
                    };
                }
            ),
    };
    const { control, formState, handleSubmit } =
        useForm<TestingProgramFormSchema>({
            resolver: zodResolver(schema),
            mode: "onBlur",
            values,
        });

    const handleSubmitClick = handleSubmit(
        async (data) => {
            const apiPayload =
                formUpdateTestingProgramToApiUpdatableTestingProgram(
                    values,
                    data
                );
            await mutateAsync({ data: apiPayload, testingProgramId });
            navigate(
                buildTestingProgramTestingControlsListUrl(testingProgramId)
            );
        },
        (error) => {
            console.error(error);
            emitNotification(
                "error",
                t("TestingProgramView_SubmitValidationError")
            );
            scrollToFirstErroredElement();
        }
    );

    return (
        <FormFooterWrapper>
            <TestingProgramForm
                control={control}
                canEdit={testingProgramDto.content.actions.edit}
                canEditAccessRights={testingProgramDto.content.actions.admin}
                users={testingProgramDto.linkedResources.users}
                blobs={testingProgramDto.linkedResources.blobs}
            />
            {testingProgramDto.content.actions.edit && (
                <StickyFooter>
                    <Button
                        size="large"
                        variant="primary"
                        disabled={!formState.isDirty}
                        loading={isPending}
                        onClick={() => void handleSubmitClick()}
                    >
                        {t("TestingProgramView_UpdateButton")}
                    </Button>
                    <ViewCancelButton
                        isDirty={formState.isDirty}
                        bodyLabel={t("TestingProgramView_ConfirmCancel_Body")}
                    />
                </StickyFooter>
            )}
        </FormFooterWrapper>
    );
}

interface DeleteTestingProgramPopupProps {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

type DeleteTestingProgramPopupActions = Omit<
    DeleteTestingProgramPopupProps,
    "open"
>;

function DeleteTestingProgramPopup({
    open,
    onCancel,
    onConfirm,
}: DeleteTestingProgramPopupProps) {
    const { t } = useTranslation();

    return (
        <DeleteWithConfirmationDialog
            title={t("ControlTesting_ProgramDelete_ConfirmTitle")}
            open={open}
            onCancel={onCancel}
            onConfirm={onConfirm}
        >
            {t("ControlTesting_ProgramDelete_ConfirmBody")}
        </DeleteWithConfirmationDialog>
    );
}
